import { HttpClient } from '@angular/common/http';
import { DestroyRef, effect, inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

interface ApiResponse<T> {
  status: number;
  message: string;
  DATA: T;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService implements OnDestroy {
  private readonly apiUrl = environment.apiUrl;
  private unreadMessagesSubject = new BehaviorSubject<number>(0);
  public unreadMessages$ = this.unreadMessagesSubject.asObservable();

  // Socket.io properties
  private socket: Socket | null = null;
  private destroy$ = new Subject<void>();

  // New message observable
  private newMessageSubject = new BehaviorSubject<any>(null);
  public newMessage$ = this.newMessageSubject.asObservable();

  private destroyRef = inject(DestroyRef);

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router
  ) {
    effect(() => {
      const isAuthenticated = this.authService.authState();
      if (isAuthenticated) {
        this.connectSocket();
        this.loadUnreadMessagesCount();
      } else {
        this.disconnectSocket();
        this.unreadMessagesSubject.next(0);
      }
    });
  }

  /**
   * Updates the seenChat timestamp for the current user
   * @returns Observable with update response
   */
  public updateSeenChat(): Observable<ApiResponse<any>> {
    const currentUser = this.authService.getCurrentUser();

    if (!currentUser || !currentUser._id) {
      throw new Error('User not authenticated');
    }

    return this.http.put<ApiResponse<any>>(`${this.apiUrl}/user/${currentUser._id}/seenChat`, {}).pipe(
      tap((res) => {
        currentUser!.seenChat = new Date(res.DATA.seenChat);
        this.authService.setCurrentUser(currentUser!);
        this.unreadMessagesSubject.next(0);
      })
    );
  }

  public updateMessageCount(count: number) {
    this.unreadMessagesSubject.next(count);

  }

  /**
   * Gets the count of unread messages for the current user
   * @returns Observable with unread count response
   */
  public getUnreadMessageCount(): Observable<ApiResponse<{ unreadCount: number }>> {
    const currentUser = this.authService.getCurrentUser();

    if (!currentUser || !currentUser._id) {
      throw new Error('User not authenticated');
    }

    return this.http.get<ApiResponse<{ unreadCount: number }>>(`${this.apiUrl}/user/${currentUser._id}/unreadMessages`).pipe(
      tap(response => {
        if (response?.DATA?.unreadCount !== undefined) {
          this.unreadMessagesSubject.next(response.DATA.unreadCount);
        }
      })
    );
  }

  /**
   * Loads the unread messages count from the API
   */
  public loadUnreadMessagesCount(): void {

    if (this.authService.isLoggedIn()) {
      this.getUnreadMessageCount().subscribe({
        next: () => { }, // Count is updated via the tap operator
        error: () => {
          this.toastService.showError('Beim Laden der ungelesenen Nachrichten ist ein Fehler aufgetreten');
        }
      });
    }

  }

  /**
   * Initialize socket connection and set up event listeners
   */
  private initializeSocket(): void {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || !currentUser._id) return;

    // Initialize socket connection
    const socketUrl = this.apiUrl.replace('/api', '');

    this.socket = io(socketUrl, {
      transports: ['websocket'],
      path: '/socket.io',
      // Called on every (re)connect, the server joins the socket to this user's room
      auth: (cb) => cb({ userId: this.authService.getCurrentUser()?._id })
    });

    // Listen for new messages
    this.socket.on('new_message', (data) => {
      this.newMessageSubject.next(data);
    });

    // Listen for unread count updates
    this.socket.on('unread_count', (data) => {
      // The message count should not be updated if the user is on the chat page
      if (this.router.url === '/chat') {
        return;
      }

      if (data && typeof data.count === 'number') {
        this.unreadMessagesSubject.next(data.count);
      }
    });

    // Handle successful connection, also fires after every reconnect
    this.socket.on('connect', () => {
      // Load unread messages count when socket connects
      this.loadUnreadMessagesCount();
    });

    // Handle connection errors
    this.socket.on('connect_error', (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'ChatService',
          action: 'socket_connect_error'
        },
        extra: {
          message: (error as Error)?.message,
          name: (error as Error)?.name,
          description: (error as any)?.description,
          context: (error as any)?.context,
          socketUrl,
          transport: this.socket?.io?.opts?.transports,
          path: this.socket?.io?.opts?.path
        }
      });
      this.toastService.showError('Beim Verbinden mit dem Socket ist ein Fehler aufgetreten');
    });
  }

  /**
   * Connect or reconnect socket if user is authenticated
   */
  public connectSocket(): void {
    if (!this.authService.isLoggedIn()) return;

    if (this.socket && !this.socket.connected) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    if (!this.socket) {
      this.initializeSocket();
    }
  }

  /**
   * Disconnects the socket
   */
  private disconnectSocket(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnectSocket();
  }
}
